import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraMetricStrip, SpecraOrgChart } from '@mono/ui-components/captains';
import { SpecraButton, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraChartLegend, SpecraEmptyState, SpecraInlineBanner, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { currentYear, holderAt, lastHolderBefore, seatExistsAt, seatLabel, yearOf, yearRange } from './model';
import { orgTone } from './orgTone';
const T = Testids.peopleMovement;
const joinNames = (holders, suffix)=>holders.length === 0 ? '—' : holders.map((h)=>`${h.contactName}${suffix ? suffix(h) : ''}`).join(' · ');
const destination = (h)=>h.wentTo?.name || h.wentToText;
/** KPI strip, tidy seat tree and legend for one organization at the end of `year`. */ export function OrgChartView({ data, year, onYearChange, selectedSeatId, onSelectSeat, onSelectPerson, canEdit, onAddSeat }) {
    const employer = data.employer;
    const employerName = employer?.name ?? 'this organization';
    const seats = useMemo(()=>[
            ...data.seats
        ].filter((s)=>(yearOf(s.retiredOn) ?? Number.POSITIVE_INFINITY) > year).sort((a, b)=>a.sortOrder - b.sortOrder || seatLabel(a).localeCompare(seatLabel(b))), [
        data.seats,
        year
    ]);
    const years = useMemo(()=>yearRange(data.seats), [
        data.seats
    ]);
    if (data.seatsNotAllowed) {
        return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
            tone: "info",
            variant: "framed",
            "data-testid": T.ownCompanyBanner,
            children: "Our own org chart lives in Delivery's org structure, not here."
        });
    }
    if (data.seats.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": T.emptySeats,
            children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: `No jobs recorded at ${employerName} yet.`,
                description: "Seats appear here once someone adds one and assigns a person to it.",
                action: canEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    onClick: onAddSeat,
                    children: "Add a seat"
                }) : undefined
            })
        });
    }
    const kpis = data.kpis;
    const nodes = seats.map((s)=>{
        const holder = holderAt(s, year);
        const left = lastHolderBefore(s, year);
        const startYear = yearOf(holder?.startDate);
        const notCreated = !seatExistsAt(s, year);
        const leftThisYear = left && yearOf(left.endDate) === year ? left : undefined;
        const cameFrom = holder?.cameFrom;
        return {
            id: s.id,
            parentId: s.parentSeatId || null,
            seat: {
                seatTitle: seatLabel(s),
                holder: holder?.contactName,
                vacantLabel: notCreated ? 'Not yet created' : left ? `Vacant since ${yearOf(left.endDate) ?? ''}`.trim() : 'Vacant',
                origin: cameFrom?.id ? {
                    label: `from ${cameFrom.name}`,
                    tone: orgTone(cameFrom)
                } : undefined,
                since: holder && !cameFrom?.id && startYear ? `since ${startYear}` : undefined,
                joinedThisPeriod: startYear === year
            },
            departure: leftThisYear ? {
                label: `${leftThisYear.contactName} → ${destination(leftThisYear) || 'left'}`,
                onActivate: ()=>onSelectPerson(leftThisYear.contactId)
            } : undefined,
            // Vacant seats are selectable too, so editors can act on them.
            selectable: true
        };
    });
    const origins = new Map();
    for (const s of seats){
        const from = holderAt(s, year)?.cameFrom;
        if (from?.id) origins.set(from.id, {
            label: from.name,
            tone: orgTone(from)
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-4)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "As of",
                size: "sm",
                testId: T.asOfYear,
                value: String(year),
                options: years.map((y)=>({
                        value: String(y),
                        label: String(y)
                    })),
                onValueChange: (v)=>onYearChange(Number(v))
            }),
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                getItemTestId: (m)=>({
                        filled: T.kpiFilled,
                        outside: T.kpiOutside,
                        joined: T.kpiJoined,
                        left: T.kpiLeft
                    })[m.id],
                metrics: [
                    {
                        id: 'filled',
                        label: `Roles filled · ${year}`,
                        value: `${kpis?.rolesFilled ?? 0} / ${kpis?.rolesTotal ?? 0}`,
                        note: employerName
                    },
                    {
                        id: 'outside',
                        label: 'Came from outside',
                        value: kpis?.cameFromOutside ?? 0,
                        note: 'of the people in these roles'
                    },
                    {
                        id: 'joined',
                        label: `Joined in ${year}`,
                        tone: 'success',
                        value: joinNames(kpis?.joined ?? [], (h)=>h.cameFrom?.name ? ` from ${h.cameFrom.name}` : ''),
                        muted: true
                    },
                    {
                        id: 'left',
                        label: `Left in ${year}`,
                        tone: 'danger',
                        value: joinNames(kpis?.left ?? [], (h)=>destination(h) ? ` to ${destination(h)}` : ''),
                        muted: true
                    }
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                "data-testid": T.orgChart,
                children: /*#__PURE__*/ _jsx(SpecraOrgChart, {
                    label: `${employerName} seats, ${year}`,
                    emptyMessage: `No jobs recorded at ${employerName} yet.`,
                    nodes: nodes,
                    selectedId: selectedSeatId,
                    onNodeSelect: (id)=>{
                        const seat = seats.find((s)=>s.id === id);
                        if (seat) onSelectSeat(seat);
                    },
                    getNodeTestId: ()=>T.seatCard
                })
            }),
            /*#__PURE__*/ _jsx(SpecraChartLegend, {
                items: [
                    ...origins.values()
                ],
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Came from:"
                })
            }),
            year === currentYear() ? null : /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                children: [
                    "Showing the seats as they stood at the end of ",
                    year,
                    "."
                ]
            })
        ]
    });
}
